import { useState } from "react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider, NewParty } from "../providers/types";
import { type PickedRecord, RecordPicker } from "./RecordPicker";
import { type ControlledDialogProps, useDialogState } from "./useDialogState";

const SELECT_CLASS =
  "border-input h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-xs";

const ERRORS: Record<string, string> = {
  name_required: "Give the deal a name",
  stage_not_in_pipeline: "That stage is not in the pipeline",
  invalid_value: "The loan amount is not a valid amount",
};

/**
 * New deal (W10 H3): name, pipeline and stage, loan amount, borrower and loan
 * officer. From a contact page the contact is the borrower; from the deal
 * board the borrower is picked. Opens the new deal after saving.
 */
export function DealCreateDialog({
  borrower: fixedBorrower,
  suggestedName = "",
  pipelineId: defaultPipelineId,
  ...dialog
}: ControlledDialogProps & {
  /** Prefilled from a contact page. */
  borrower?: PickedRecord;
  suggestedName?: string;
  /** The board's current pipeline. */
  pipelineId?: string;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const notify = useNotify();
  const { isOpen, setIsOpen, showTrigger } = useDialogState(dialog);
  const [name, setName] = useState(suggestedName);
  const [pipelineId, setPipelineId] = useState(defaultPipelineId ?? "");
  const [stageId, setStageId] = useState("");
  const [amount, setAmount] = useState("");
  const [borrower, setBorrower] = useState<PickedRecord | null>(
    fixedBorrower ?? null,
  );
  const [loanOfficer, setLoanOfficer] = useState<PickedRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const pipelines = useGetList<{ id: string; name: string }>(
    "pipelines",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "sort_index", order: "ASC" },
    },
    { enabled: isOpen },
  );
  const pipeline = pipelineId || pipelines.data?.[0]?.id || "";
  const stages = useGetList<{ id: string; label: string }>(
    "pipeline_stages",
    {
      filter: { pipeline_id: pipeline },
      pagination: { page: 1, perPage: 100 },
      sort: { field: "sort_index", order: "ASC" },
    },
    { enabled: isOpen && Boolean(pipeline) },
  );

  const close = () => {
    setIsOpen(false);
    setName(suggestedName);
    setStageId("");
    setAmount("");
    setBorrower(fixedBorrower ?? null);
    setLoanOfficer(null);
  };

  const save = async () => {
    const parties: NewParty[] = [];
    if (borrower) {
      parties.push({
        contact_id: borrower.id,
        role: "borrower",
        is_primary: true,
      });
    }
    if (loanOfficer) {
      parties.push({
        contact_id: loanOfficer.id,
        role: "loan_officer",
        is_primary: true,
      });
    }
    setIsSaving(true);
    try {
      const { id } = await dataProvider.createDeal({
        name,
        pipeline_id: pipeline,
        stage_id: stageId || undefined,
        amount_cents: amount.trim() ? Math.round(Number(amount) * 100) : null,
        parties,
      });
      close();
      navigate(`/deals/${id}/show`);
    } catch (error) {
      const code = (error as { body?: { error?: string } }).body?.error ?? "";
      notify(ERRORS[code] ?? "The deal could not be saved", { type: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {showTrigger ? (
        <Button size="sm" onClick={() => setIsOpen(true)}>
          New deal
        </Button>
      ) : null}
      <Dialog open={isOpen} onOpenChange={(open) => (open ? null : close())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New deal</DialogTitle>
            <DialogDescription>
              Its branch and team come from the loan officer's team.
            </DialogDescription>
          </DialogHeader>
          <form
            aria-label="New deal"
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <div className="space-y-1">
              <Label htmlFor="deal-name">Name</Label>
              <Input
                id="deal-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="deal-pipeline">Pipeline</Label>
                <select
                  id="deal-pipeline"
                  className={SELECT_CLASS}
                  value={pipeline}
                  onChange={(e) => {
                    setPipelineId(e.target.value);
                    setStageId("");
                  }}
                >
                  {(pipelines.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="deal-stage">Stage</Label>
                <select
                  id="deal-stage"
                  className={SELECT_CLASS}
                  value={stageId || stages.data?.[0]?.id || ""}
                  onChange={(e) => setStageId(e.target.value)}
                >
                  {(stages.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="deal-amount">Loan amount ($, optional)</Label>
              <Input
                id="deal-amount"
                type="number"
                min="0"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            {fixedBorrower ? (
              <p className="text-sm">
                Borrower: <strong>{fixedBorrower.label}</strong>
              </p>
            ) : (
              <RecordPicker
                id="deal-borrower"
                label="Borrower (optional)"
                objectType="contact"
                picked={borrower}
                onPick={setBorrower}
                pickedText="Borrower:"
              />
            )}
            <RecordPicker
              id="deal-lo"
              label="Loan officer (optional)"
              objectType="contact"
              picked={loanOfficer}
              onPick={setLoanOfficer}
              pickedText="Loan officer:"
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || !name.trim() || !pipeline}
              >
                Save deal
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
